import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import MenuItem from "./components/MenuItem/MenuItem";
import useAuthorization from "@/hooks/useAuthorization";
import {getMenuConfig} from "@/components/Layout/components/SideMenu/menuConfig";
import logoutSrc from "@/assets/images/menu_assets/menu_topbar-icons/topbar_icon-logout-default_desktop+mobile.svg";
import {useAuth} from "@/hooks/useAuth.tsx";
import notifySrc
    from "@/assets/images/menu_assets/menu_topbar-icons/topbar_icon-notification-default_desktop+mobile.svg";
import {Routes} from "@/routes/router.tsx";
import {useNavigate} from "react-router-dom";
import NotificationBadge from "@/components/custom/NotificationBadge";
import HelpDrawer from "@/components/Help/HelpDrawer";

// Props for MobileSideMenu
interface MobileSideMenuProps {
    isVisible: boolean;
    onClose: () => void; // Function to close the menu
}

const MobileSideMenu: React.FC<MobileSideMenuProps> = ({ isVisible, onClose }) => {
    const { t } = useTranslation();
    const { logout } = useAuth();
    const { hasLevel } = useAuthorization();
    const navigate = useNavigate();
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    const handleLogout = async () => {
        try {
            await logout(); // Log out the user using the provided `useAuth` hook
        } catch (error) {
            console.error("Error during logout:", error);
        }
    };

    const handleNavigateToNotifications = () => {
        navigate(Routes.PORTAL_NOTIFICATIONS);
    };

    const menuConfig = getMenuConfig(t, hasLevel);

    return (
        <aside
            className={`fixed top-0 right-0 z-50 bg-[#F1EDE7] w-[250px] h-full p-4 transform transition-transform duration-300 ${
                isVisible ? "translate-x-0" : "translate-x-full"
            }`}
        >
            <div className="flex justify-between items-center mb-4">
                <button
                    className="text-gray-600 hover:text-black"
                    onClick={onClose}
                    aria-label="Close menu"
                >
                    ✖
                </button>
            </div>
            <nav className="pt-4">
                <ul className="w-full">
                    {menuConfig.map((item, index) => (
                        <MenuItem key={index} {...item} onClick={onClose} />
                    ))}
                </ul>
                <button
                    type="button"
                    onClick={() => {
                        setIsHelpOpen(true);
                        onClose();
                    }}
                    className="flex w-full items-center text-black hover:bg-light-bunker cursor-pointer rounded-lg px-4 py-2 mt-2"
                >
                    <QuestionMarkCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span className="text-sm truncate">{t('sidebar.menu.help')}</span>
                </button>
            </nav>
            <HelpDrawer
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
                mode="general"
            />
            <div className="flex justify-between items-center mt-6">
                <div
                    className="flex flex-col items-center cursor-pointer hover:cursor-pointer"
                    onClick={handleLogout}
                >
                    <img src={logoutSrc} width={15} alt="Logout" />
                    <span className="text-sm text-gray-600">{t('header.menu.log-out')}</span>
                </div>
                {/* Notifications - Only show for users with player level */}
                {hasLevel('player') && (
                    <div
                        className="flex flex-col items-center cursor-pointer hover:cursor-pointer relative"
                        onClick={handleNavigateToNotifications}
                    >
                        <div className="relative">
                            <img src={notifySrc} width={15} alt="Notifications" />
                            <NotificationBadge />
                        </div>
                        <span className="text-sm text-gray-600">{t('header.menu.notifications')}</span>
                    </div>
                )}
            </div>
        </aside>
    );
};

export default MobileSideMenu;
